import { h, vaciar, descargarFichero, num } from '../dom';
import type { ClienteDb } from '../../db/cliente';
import type { Fila } from '../../db/tipos';
import type { TablaExportable } from '../../db/consultas';

const EJEMPLO = `-- Parcelas de 300 a 500 m2 con una construccion de 50 a 60 m2 (huella o construida)
SELECT p.rc, p.area_m2, e.huella_m2, e.sup_construida_m2, e.anio, e.uso
FROM parcelas p JOIN edificios e ON e.rc = p.rc
WHERE p.area_m2 BETWEEN 300 AND 500
  AND (e.huella_m2 BETWEEN 50 AND 60 OR e.sup_construida_m2 BETWEEN 50 AND 60)
ORDER BY ABS(p.area_m2 - 400) + ABS(e.huella_m2 - 55)`;

const TABLAS: TablaExportable[] = ['parcelas', 'edificios', 'partes', 'inmuebles', 'candidatos', 'hidrografia'];

function aCsv(filas: Fila[]): string {
  if (filas.length === 0) return '';
  const columnas = Object.keys(filas[0]);
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columnas.join(';'), ...filas.map((f) => columnas.map((c) => esc(f[c])).join(';'))].join('\r\n');
}

/** Filas con columna `geom` (GeoJSON en UTM) a FeatureCollection con el CRS declarado. */
function aGeoJson(filas: Fila[], epsg: number | null): string {
  const features = filas.map((f) => {
    const { geom, ...props } = f;
    let geometry: unknown = null;
    try { geometry = geom ? JSON.parse(String(geom)) : null; } catch { geometry = null; }
    return { type: 'Feature', geometry, properties: props };
  });
  const crs = epsg ? { type: 'name', properties: { name: `urn:ogc:def:crs:EPSG::${epsg}` } } : undefined;
  return JSON.stringify({ type: 'FeatureCollection', crs, features });
}

/** Ventana "Avanzado": consola SQL de solo lectura y exportacion de tablas. */
export function abrirAvanzado(db: ClienteDb, epsg: number | null): void {
  const cerrar = () => fondo.remove();
  const editor = h('textarea', { rows: 8, style: 'width:100%;font-family:ui-monospace,Menlo,Consolas,monospace', value: EJEMPLO });
  const salida = h('div', { style: 'overflow:auto;max-height:40vh' });
  const estado = h('span', { class: 'silencio' });
  let ultimas: Fila[] = [];

  const ejecutar = async () => {
    vaciar(salida);
    estado.textContent = 'Ejecutando…';
    try {
      ultimas = await db.consultaLibre(editor.value);
      estado.textContent = `${num(ultimas.length)} fila(s)${ultimas.length >= 1000 ? ' (limitado a 1.000)' : ''}`;
      if (ultimas.length) {
        const columnas = Object.keys(ultimas[0]);
        salida.append(h('table', { class: 'tabla' },
          h('thead', null, h('tr', null, ...columnas.map((c) => h('th', null, c)))),
          h('tbody', null, ...ultimas.slice(0, 200).map((f) => h('tr', null, ...columnas.map((c) => h('td', null, String(f[c] ?? '').slice(0, 80))))))));
      }
    } catch (err) {
      estado.textContent = '';
      salida.append(h('div', { class: 'error' }, err instanceof Error ? err.message : String(err)));
    }
  };

  const fondo = h('div', { class: 'modal-fondo', onClick: (ev: Event) => { if (ev.target === fondo) cerrar(); } },
    h('div', { class: 'modal ancha' },
      h('div', { class: 'ficha-cabecera' }, h('h2', null, 'Avanzado: consola SQL y exportación'), h('button', { class: 'cerrar', onClick: cerrar }, '×')),
      h('p', { class: 'ayuda' }, 'Tablas: parcelas (rc, etiqueta, area_m2, cx, cy, n_edificios, n_partes, geom, epsg, dist_agua_m, agua_nombre, agua_id), hidrografia (id, nombre, tipo, persistencia, orden, ancho_max_m, longitud_m, geom, epsg), edificios (rc, uso, estado, anio, plantas, unidades, viviendas, sup_construida_m2, huella_m2, foto_url, geom, alero_m, cumbrera_m, huella_corregida, desviacion_m), partes (id, rc, plantas_sobre, plantas_bajo, altura_bajo_m, huella_m2, geom), inmuebles (rc20, rc, direccion, planta, puerta, superficie_m2, uso, anio), candidatos (rc, anuncio_url, precio, etiqueta, notas). Solo SELECT.'),
      editor,
      h('div', { class: 'fila-botones' },
        h('button', { class: 'boton primario', onClick: () => void ejecutar() }, 'Ejecutar'),
        h('button', { class: 'boton secundario', onClick: () => { if (ultimas.length) descargarFichero('consulta-cernicalo.csv', '﻿' + aCsv(ultimas), 'text/csv;charset=utf-8'); } }, 'Resultado a CSV'),
        estado),
      salida,
      h('h3', null, 'Exportar tablas completas'),
      h('div', { class: 'fila-botones' },
        ...TABLAS.map((t) => h('button', { class: 'boton secundario', onClick: async () => {
          const filas = await db.exportarTabla(t);
          descargarFichero(`${t}-cernicalo.csv`, '﻿' + aCsv(filas), 'text/csv;charset=utf-8');
        } }, `${t} CSV`)),
        ...(['parcelas', 'edificios', 'partes', 'hidrografia'] as const).map((t) => h('button', { class: 'boton secundario', onClick: async () => {
          const filas = await db.exportarTabla(t);
          descargarFichero(`${t}-cernicalo.geojson`, aGeoJson(filas, epsg), 'application/geo+json');
        } }, `${t} GeoJSON (UTM)`))),
      h('p', { class: 'silencio' }, 'El GeoJSON sale en el huso UTM de los datos (no en WGS84), con el CRS declarado; QGIS lo abre directamente.')));
  document.body.append(fondo);
  void ejecutar();
}
